<template>
  <b-card class="shadow-sm h-100">
    <b-card-title
      class="mb-2 px-6 pt-6 d-flex justify-content-between align-items-start"
    >
      <div>
        <b-icon icon="cash-stack" variant="primary" class="mr-2" />
        <span>Caixa aberto agora</span>
      </div>

      <button
        type="button"
        class="btn btn-light btn-sm font-weight-bold text-primary"
        :disabled="!abertos"
        @click="abrirModalCaixas"
      >
        {{ abertos }} {{ abertos === 1 ? "caixa" : "caixas" }}
      </button>
    </b-card-title>

    <b-card-body class="pl-6 pr-6 pb-4 pt-3">
      <template v-if="loading">
        <b-skeleton width="56%" height="36px" class="mb-4" />
        <b-skeleton width="44%" height="14px" class="mb-5" />
        <b-skeleton width="100%" height="18px" class="mb-3" />
        <b-skeleton width="78%" height="18px" />
      </template>

      <template v-else>
        <h2 class="display-4 text-primary mb-0 font-weight-bold">
          {{ formatCurrency(valorTotal) }}
        </h2>
        <div
          class="text-muted font-weight-bold mb-5"
          title="Abertura + vendas e recebimentos físicos + suprimentos − sangrias. Inclui dinheiro e cheques."
        >
          Saldo na gaveta
        </div>

        <div class="caixa-summary mb-4">
          <div>
            <span class="text-muted font-weight-bold">Abertos hoje</span>
            <strong>{{ abertosHoje }}</strong>
          </div>
          <div>
            <span class="text-muted font-weight-bold">Mais antigo</span>
            <strong>{{ maisAntigoLabel }}</strong>
          </div>
        </div>

        <div v-if="!abertos" class="text-muted font-weight-bold">
          Nenhum caixa aberto neste momento.
        </div>
        <button
          v-else
          type="button"
          class="btn btn-link btn-sm p-0 font-weight-bold text-primary"
          @click="abrirModalCaixas"
        >
          Ver composição por caixa
        </button>
      </template>
    </b-card-body>

    <b-modal
      :id="modalId"
      title="Composição do saldo por caixa"
      size="lg"
      scrollable
      hide-footer
    >
      <div v-if="caixas.length">
        <section
          v-for="caixa in caixas"
          :key="caixa.id"
          class="caixa-detalhe mb-5"
        >
          <div class="d-flex justify-content-between mb-3">
            <strong>Caixa #{{ caixa.id }}</strong>
            <span class="text-muted">{{
              formatDate(caixa.data_abertura)
            }}</span>
          </div>
          <div
            v-for="alerta in caixa.alertas"
            :key="alerta"
            class="alert alert-warning py-2"
            role="alert"
          >
            {{ alerta }}
          </div>
          <table class="table table-sm mb-3">
            <caption class="sr-only">
              Composição do saldo na gaveta
            </caption>
            <tbody>
              <tr>
                <td>Abertura (+)</td>
                <td class="text-right">
                  {{ formatCurrency(caixa.valor_abertura) }}
                </td>
              </tr>
              <tr>
                <td>Vendas em dinheiro ou cheque (+)</td>
                <td class="text-right">
                  {{ formatCurrency(caixa.vendas_fisicas) }}
                </td>
              </tr>
              <tr>
                <td>Contas recebidas em dinheiro ou cheque (+)</td>
                <td class="text-right">
                  {{ formatCurrency(caixa.recebimentos_fisicos) }}
                </td>
              </tr>
              <tr>
                <td>Suprimentos (+)</td>
                <td class="text-right">
                  {{ formatCurrency(caixa.suprimento) }}
                </td>
              </tr>
              <tr>
                <td>Sangrias (−)</td>
                <td class="text-right">{{ formatCurrency(caixa.sangria) }}</td>
              </tr>
              <tr class="bg-light font-weight-bold">
                <th scope="row">Saldo na gaveta (=)</th>
                <td class="text-right">
                  {{ formatCurrency(caixa.saldo_gaveta) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="bg-light rounded p-3">
            <div class="d-flex justify-content-between">
              <span>Recebimentos eletrônicos</span>
              <strong>{{
                formatCurrency(caixa.total_recebimentos_eletronicos)
              }}</strong>
            </div>
            <small class="text-muted d-block mt-1">
              Pix, cartões e benefícios. Não fazem parte do saldo na gaveta.
            </small>
          </div>
        </section>
        <div v-if="abertos > caixas.length" class="text-muted mt-4">
          Exibindo os primeiros {{ caixas.length }} de {{ abertos }} caixas
          abertos.
        </div>
      </div>

      <div v-else class="text-center text-muted py-6">
        Nenhum caixa aberto neste momento.
      </div>
    </b-modal>
  </b-card>
</template>

<script>
export default {
  name: "CaixaAberto",
  props: {
    dados: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  computed: {
    modalId() {
      return `modal-caixas-abertos-${this._uid}`;
    },
    abertos() {
      return Number(this.dados?.abertos || 0);
    },
    abertosHoje() {
      return Number(this.dados?.abertos_hoje || 0);
    },
    valorTotal() {
      if (this.dados?.total_da_gaveta != null) {
        return Number(this.dados.total_da_gaveta);
      }
      if (!this.abertos) return 0;

      // Respostas anteriores já traziam a gaveta por caixa, mas misturavam
      // eletrônicos no total do card. Só podemos somar se a lista estiver completa.
      if (
        this.caixas.length === this.abertos &&
        this.caixas.every((caixa) => caixa.saldo_gaveta != null)
      ) {
        return this.caixas.reduce(
          (total, caixa) => total + Number(caixa.saldo_gaveta),
          0
        );
      }
      return null;
    },
    maisAntigoLabel() {
      return this.formatDate(this.dados?.mais_antigo);
    },
    caixas() {
      const caixas = Array.isArray(this.dados?.caixas) ? this.dados.caixas : [];
      return caixas.map((caixa) => {
        const conferencia = caixa.conferencia || {};
        return {
          ...caixa,
          vendas_fisicas:
            caixa.vendas_fisicas ?? conferencia.vendas_fisicas ?? null,
          recebimentos_fisicos:
            caixa.recebimentos_fisicos ??
            conferencia.recebimentos_fisicos ??
            null,
          suprimento: caixa.suprimento ?? conferencia.suprimento ?? null,
          sangria: caixa.sangria ?? conferencia.sangria ?? null,
          saldo_gaveta:
            caixa.total_da_gaveta ?? conferencia.total_da_gaveta ?? null,
          total_recebimentos_eletronicos:
            caixa.total_recebimentos_eletronicos ??
            conferencia.recebimentos_eletronicos ??
            null,
          alertas: conferencia.alertas || [],
        };
      });
    },
  },
  methods: {
    abrirModalCaixas() {
      if (!this.abertos) return;

      this.$bvModal.show(this.modalId);
    },
    formatCurrency(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value) || 0);
    },
    formatDate(value) {
      if (!value) return "Sem abertura";

      const date = this.parseDate(value);
      if (!date) return "Sem abertura";

      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    parseDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;

      if (typeof value !== "string") return date;

      const timestamp = value.trim();
      const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
      if (hasTimezone) return date;

      // Versões anteriores do PDV Desktop enviavam UTC para uma coluna sem
      // fuso. Uma abertura não pode estar horas no futuro; nesse caso, recupera
      // o instante UTC original. Datas locais normais do PDV Web são mantidas.
      const tolerance = 5 * 60 * 1000;
      if (date.getTime() <= Date.now() + tolerance) return date;

      const utcDate = new Date(`${timestamp.replace(" ", "T")}Z`);
      if (
        Number.isNaN(utcDate.getTime()) ||
        utcDate.getTime() > Date.now() + tolerance
      ) {
        return date;
      }

      return utcDate;
    },
  },
};
</script>

<style scoped>
.display-4 {
  line-height: 1.1;
}

.caixa-summary {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.caixa-summary strong,
.caixa-summary span {
  display: block;
}

.caixa-summary strong {
  color: #3f4254;
  margin-top: 0.25rem;
}
</style>
